import { Button, Table, Tag, Tooltip } from "antd"
import { notification as toast } from "antd"
import Title from "antd/es/typography/Title"
import Layout from "modules/layout/dashboard"
import { formatDay } from "utils/format-datetime"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { ReactComponent as ShowIcon } from "assets/icons/show.svg"

import { useTranslation } from "react-i18next"
import { usePopup } from "utils/use-popup"
import { useMember } from "pages/member/queries"
import { useMemberId } from "pages/member"
import { useCart, useConfirmCart } from "pages/cart/queries"
import DeletePresubscriptionModal from "./delete"
import { IPresubscription } from "./types"
import { useNavigate } from "react-router-dom"
import { formatCurrency } from "utils/format-currency"

const { Main, Header } = Layout

const Cart: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const memberId = useMemberId()
  const member = useMember(memberId)
  const { data, isLoading } = useCart(memberId)
  const deletePresubscriptionModal = usePopup<IPresubscription>()
  const confirmCart = useConfirmCart(memberId!, {
    onSuccess(data: any) {
      toast.success({
        message: t("cart.confirm_cart_success_msg"),
        placement: "top",
      })
      if (data.data.invoice_id)
        navigate(`/members/${memberId}/invoices/${data.data.invoice_id}`)
    },
  })

  return (
    <Layout>
      <Header />
      <Main>
        <Main.PageHeader
          breadcrumbs={[
            { title: member.data?.name, link: `/members/${member.data?.id}` },
            { title: t("global.cart") },
          ]}
          title={
            <>
              <Title level={3}>{t("global.cart")}</Title>
              <Title level={5} style={{ marginTop: 0 }}>{`${t(
                "global.total"
              )}: ${formatCurrency(
                data
                  ? data.reduce(
                      (prev, curr) =>
                        parseFloat(curr.event.activity_unit_price_offer ?? 0) +
                        prev,
                      0
                    )
                  : 0
              )} (${t("cart.this_is_not_an_invoice")})`}</Title>
            </>
          }
          rightContent={
            <Button
              size="large"
              onClick={() => confirmCart.mutate()}
              loading={confirmCart.isLoading}
              disabled={data?.length === 0}
            >
              {t("global.confirmX", {
                entity: t("events.presubscription"),
                context: "female",
                count: 1,
              })}
            </Button>
          }
        />
        <Table
          tableLayout="fixed"
          pagination={false}
          rowKey={"id"}
          dataSource={data}
          loading={isLoading}
          columns={[
            {
              title: t<string>("events.activities"),
              dataIndex: ["event", "name"],
              key: "name",
              ellipsis: true,

              render: (value, record) => (
                <Tooltip placement="bottomLeft" title={value}>
                  {record.activity_count
                    ? `${value} (${record.activity_count})`
                    : value}
                </Tooltip>
              ),
            },
            {
              title: t<string>("events.title"),
              dataIndex: "description",
              key: "description",
              ellipsis: true,
              render: (value, record) => (
                <Tooltip placement="bottomLeft" title={value}>
                  {record.activity_count
                    ? `${formatDay(record.oldest_start_date)} - ${formatDay(
                        record.latest_end_date
                      )}`
                    : `${formatDay(record.event.start_date)}, ${
                        record.event.start_hour
                      }h - ${record.event.end_hour}h`}
                </Tooltip>
              ),
            },
            {
              title: t<string>("events.subscription_price"),
              dataIndex: ["event", "activity_unit_price_offer"],
              key: "activity_unit_price_offer",
              ellipsis: true,
              render: (value) =>
                typeof value !== "number" && !value ? (
                  <Tag color="success">{t("global.free")}</Tag>
                ) : (
                  <Tooltip placement="bottomLeft" title={formatCurrency(value)}>
                    {formatCurrency(value)}
                  </Tooltip>
                ),
            },
            {
              title: t<string>("global.actions"),
              key: "action",
              width: 100,
              render: (_, record) => (
                <>
                  <Tooltip placement="bottom" title={t("global.show")}>
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() =>
                        navigate(`../events/show/${record.event.id}`)
                      }
                      icon={<ShowIcon />}
                    />
                  </Tooltip>
                  <Tooltip
                    placement="bottom"
                    title={t("global.deleteX", {
                      entity: t("events.presubscription"),
                      context: "female",
                      count: 1,
                    })}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() =>
                        deletePresubscriptionModal.open(record as any)
                      }
                      icon={<DeleteIcon />}
                    />
                  </Tooltip>
                </>
              ),
            },
          ]}
        />
        <DeletePresubscriptionModal {...deletePresubscriptionModal} />
      </Main>
    </Layout>
  )
}

export default Cart
